import { message } from 'antd'

import { Client, LoginId, PlayerId, TeamId } from '@suntex.fim/api-client'

export { parseUrl, fromQueryString, toQueryString } from '@suntex.core/utils'

//match the property getter format "return x.propName;" OR "x => x.propName"
//note that the semicolon is optional, because it can be removed by minification
//TODO: there could be other function formats that break this, so may need to revisit
const rgxExpressionPropertyName = [
  /return \w+\.(\w+);?/,
  /\(?\w+\)?\s*=>\s*\w+\.(\w+);?/
]

export function propertyNames<T>(
  obj: T,
  ...funcs: ((x: T) => any)[]
): string[] {
  return funcs.map((f) => propertyName(obj, f))
}

export function propertyName<T, TResult>(
  _obj: T,
  func: (x: T) => TResult
): string {
  //the function as a string
  const funcCode = func.toString()

  //for each rgx format
  for (const rgx of rgxExpressionPropertyName) {
    //do the match
    const match = funcCode.match(rgx)

    const EXPECTED_MATCH_LENGTH = 2 // 0 is the whole match, 1 is the captured group
    //if we got a valid match with 1 numbered match (0 is the whole thing)
    if (match != null && match.length == EXPECTED_MATCH_LENGTH) {
      //because of the format of the regex, the second match is the property name
      return match[1]
    }
  }

  //if we got here, no match
  throw new Error(
    `The expression '${funcCode}' could not be parsed for a property name.`
  )
}

function defineProperty(prototype: object, name: string, fn: Function) {
  Object.defineProperty(prototype, name, {
    enumerable: false,
    value: fn
  })
}

export function definePrototypeProperty(
  object: { prototype: object },
  name: string,
  fn: Function
) {
  Object.defineProperty(object.prototype, name, {
    enumerable: false,
    value: fn
  })
}

declare global {
  interface Array<T> {
    //toKeyLabelOptions(): Array<{key: number, label: string}>

    //some linq friendly aliases for functions that do exist
    where(
      predicate: (value: T, index: number, array: T[]) => unknown,
      thisArg?: unknown
    ): T[]
    select<U>(
      callbackfn: (value: T, index: number, array: T[]) => U,
      thisArg?: unknown
    ): U[]
  }
}

export function isNullOrUndefined(obj: unknown): boolean {
  return obj === null || obj === undefined
}

export function explicitCast<T>(obj: unknown): T {
  return obj as T
}

export function valueAsIntOrDefault(
  value: string | number | null | undefined,
  defaultValue = 0
): number {
  if (value === null || value === undefined) return defaultValue

  //parse int
  const intValue = parseInt(`${value}`)

  //if not a number at all or not an integer, return the default, otherwise return the value
  return isNaN(intValue) || !Number.isInteger(intValue)
    ? defaultValue
    : intValue
}

export function isFunction(obj: unknown): boolean {
  return obj instanceof Function
}

export const generatePassword = async (api: Client, loginId: LoginId) => {
  try {
    const response = await api.post_Login_RegeneratePassword({
      loginId
    })
    return response
  } catch (error) {
    message.error('Unable to generate new password for this player.')
  }
}

function formatArgs(...args: unknown[]): string {
  return args
    .map((arg) => {
      if (arg instanceof Error) {
        return arg.stack || arg.toString()
      }

      if (typeof arg === 'object' && arg !== null) {
        const JSON_INDENT_SPACES = 2
        try {
          return JSON.stringify(arg, null, JSON_INDENT_SPACES)
        } catch (e) {
          return '[Circular]'
        }
      }

      return String(arg)
    })
    .join(' ')
}

export function appShowError(errorMessage?: unknown, ...args: unknown[]): void {
  console.error(errorMessage, ...args)

  message.error(formatArgs(errorMessage, ...args))
}

export function resolveTeamId(
  teamIdFromUrl?: number,
  teamInfo?: { id?: number },
  user?: { entityId?: number; teamId?: number }
): TeamId {
  return (Number(teamIdFromUrl) ||
    teamInfo?.id ||
    user?.teamId ||
    user?.entityId ||
    0) as TeamId
}

/**
 * Type guard to check if value is a valid number
 */
export const isValidNumber = (value: unknown): value is number =>
  typeof value === 'number'

/**
 * Type guard to check if value is a valid string
 */
export const isValidString = (value: unknown): value is string =>
  typeof value === 'string'

/**
 * Type guard to check if value is a valid object
 */

export const isValidObject = (value: unknown): value is object =>
  typeof value === 'object' && value !== null

export function areSetsEqual<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false

  const setA = new Set(a)
  if (setA.size !== b.length) return false // handles duplicates

  return b.every((item) => setA.has(item))
}
